<script lang="ts" setup>
import _ from 'lodash'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import { ListedSavedSearch, SavedSearchCategory, SearchGroup, StayListUrlParams } from '@/saved-searches/types'
import { savedSearchLabels } from '@/saved-searches/utils'
import { ModeEnum, PmsiTypeEnum, PrivacyEnum } from '@/store/modules/settings/types'

import GenericThumbnailList from './GenericThumbnailList.vue'
import HomePageQuickFilter from './HomePageQuickFilter.vue'
import OwnSearchGroupList from './OwnSearchGroupList.vue'
import OwnSearchList from './OwnSearchList.vue'
import RecentlyUsedSearchList from './RecentlyUsedSearchList.vue'
import SancareCategoryList from './SancareCategoryList.vue'
import TeamSearchList from './TeamSearchList.vue'

type QuickFilterResults = {
  privateSearches: number[] | null
  userSearchGroups: number[] | null
  teamSearches: number[] | null
  publicCategories: number[] | null
  sancareCategories: number[] | null
  recentlyUsedSearches: number[] | null
  earlylimit: string | null
  latelimit: string | null
}

const EMPTY_QUICK_FILTER: QuickFilterResults = {
  privateSearches: null,
  userSearchGroups: null,
  teamSearches: null,
  publicCategories: null,
  sancareCategories: null,
  recentlyUsedSearches: null,
  earlylimit: null,
  latelimit: null,
}

const store = useStore()

const props = withDefaults(defineProps<{
  mode: ModeEnum
  privateSearches: ListedSavedSearch[]
  teamSearches: ListedSavedSearch[]
  userSearchGroups: SearchGroup[]
  publicCategories: SavedSearchCategory[]
  sancareCategories: SavedSearchCategory[]
  recentlyUsedSearches?: ListedSavedSearch[]
  earlyLimit: object
  lateLimit: object
  pmsiType?: PmsiTypeEnum
}>(), {
  pmsiType: PmsiTypeEnum.MCO,
  recentlyUsedSearches: () => [],
})

const emit = defineEmits<{
  (e: 'refresh-stats', payload: unknown): void
}>()

const selectedSearches = ref<Pick<ListedSavedSearch, 'id'>[]>([])
const quickFilterResults = ref<QuickFilterResults>({ ...EMPTY_QUICK_FILTER })

const settingsUrlQuery = computed(() => store.getters.settingsUrlQuery)
const savedSearchesRequest = computed(() => store.state.savedSearches.fetchSavedSearchesRequest)

const searchLabels = computed(() => savedSearchLabels(props.mode))

const selectedSearchLabel = computed(() => {
  if (selectedSearches.value.length <= 1) {
    return `Utiliser la ${searchLabels.value.singular} selectionnée`
  }
  return `Utiliser les ${searchLabels.value.plural} selectionnées`
})

const selectedSearchQuery = computed<StayListUrlParams>(() => ({
  mode: props.mode,
  searchId: _.map(selectedSearches.value, 'id'),
  ...settingsUrlQuery.value,
}))

function onQuickFilterUpdate(results: QuickFilterResults) {
  selectedSearches.value = []
  quickFilterResults.value = results
}

function disableQuickFilter() {
  selectedSearches.value = []
  quickFilterResults.value = { ...EMPTY_QUICK_FILTER }
}

function handleSelectSearch(search: Pick<ListedSavedSearch, 'id'>) {
  if (_.find(selectedSearches.value, (s) => s.id === search.id)) {
    selectedSearches.value = _.filter(selectedSearches.value, (s) => s.id !== search.id)
  } else {
    selectedSearches.value = _.concat(selectedSearches.value, search)
  }
}

function refreshStats(payload: unknown) {
  emit('refresh-stats', payload)
}
</script>

<template>
  <div>
    <div class="row">
      <div class="col-md-3 col text-end">
        <router-link
          v-if="selectedSearches.length !== 0"
          class="btn btn-link"
          name="apply-selected-searches"
          :to="{ name: 'staylist', query: selectedSearchQuery, params: {pmsiType: pmsiType} }"
        >
          {{ selectedSearchLabel }}
        </router-link>
      </div>
    </div>
    <generic-thumbnail-list
      :mode="mode"
      :pmsi-type="pmsiType"
    />
    <home-page-quick-filter
      :key="mode"
      :mode="mode"
      :private-searches="privateSearches"
      :team-searches="teamSearches"
      :public-categories="publicCategories"
      :user-search-groups="userSearchGroups"
      :sancare-categories="sancareCategories"
      :recently-used-searches="recentlyUsedSearches"
      class="col"
      @quick-filter-update="onQuickFilterUpdate"
      @quick-filter-disable="disableQuickFilter"
    />
    <recently-used-search-list
      v-if="recentlyUsedSearches.length"
      :search-list="recentlyUsedSearches"
      :selected-searches="selectedSearches"
      :quick-filter-results="quickFilterResults.recentlyUsedSearches"
      @select-search="handleSelectSearch"
      @refresh-stats="refreshStats"
    />
    <own-search-list
      :search-list="privateSearches"
      :mode="mode"
      :pmsi-type="pmsiType"
      :early-limit="earlyLimit"
      :late-limit="lateLimit"
      :selected-searches="selectedSearches"
      :quick-filter-results="quickFilterResults.privateSearches"
      :title="`Mes ${searchLabels.plural}`"
      :can-add-own-search="true"
      @select-search="handleSelectSearch"
      @refresh-stats="refreshStats"
    />
    <!-- SMR not supported for now -->
    <own-search-group-list
      v-if="mode === ModeEnum.OPTIMIZABLE && pmsiType === PmsiTypeEnum.MCO"
      :search-groups="userSearchGroups"
      :mode="mode"
      :selected-searches="selectedSearches"
      :quick-filter-results="quickFilterResults.userSearchGroups"
      :pmsi-type="pmsiType"
      title="Mes groupes de recherches"
      @refresh-stats="refreshStats"
    />
    <team-search-list
      v-if="teamSearches.length"
      :team-searches="teamSearches"
      :mode="mode"
      :pmsi-type="pmsiType"
      :selected-searches="selectedSearches"
      :quick-filter-results="quickFilterResults.teamSearches"
      title="Recherches de mon équipe"
      @select-search="handleSelectSearch"
      @refresh-stats="refreshStats"
    />
    <sancare-category-list
      :pmsi-type="pmsiType"
      :sancare-categories="publicCategories"
      :mode="mode"
      :selected-searches="selectedSearches"
      :quick-filter-results="quickFilterResults.publicCategories"
      :searches-privacy="PrivacyEnum.PUBLIC_SEARCH"
      @select-search="handleSelectSearch"
      @refresh-stats="refreshStats"
    />
    <sancare-category-list
      :pmsi-type="pmsiType"
      :sancare-categories="sancareCategories"
      :mode="mode"
      :selected-searches="selectedSearches"
      :quick-filter-results="quickFilterResults.sancareCategories"
      :searches-privacy="PrivacyEnum.SANCARE_SEARCH"
      @select-search="handleSelectSearch"
      @refresh-stats="refreshStats"
    />
    <div v-if="savedSearchesRequest.fetching">
      <div class="loader loader-lg loader-center" />
    </div>
  </div>
</template>
